// AppointmentsList.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaidIcon from "@mui/icons-material/Paid";
import AddBusinessIcon from "@mui/icons-material/Business";
import ApiServices from "../../../Services/ApiServices/ApiServices";

// Base URL to prefix the image paths (as you requested)
const BASE_URL = "http://127.0.0.1:8000";

const AppointmentsList = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI filters
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all"); // all | paid | unpaid

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // Debounce search
  const searchRef = useRef(null);

  // Assign business dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAppointment, setAssignAppointment] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [costToBusiness, setCostToBusiness] = useState("");
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const res = await ApiServices.get("service-appointments/");
      const data = res?.data ?? res;
      if (!Array.isArray(data)) {
        setAppointments([]);
        setSnack({ open: true, message: "Unexpected data from server", severity: "error" });
      } else {
        // Convert scheduled_at to Date for sorting and keep original structure
        const normalized = data.map((a) => ({ ...a, _scheduled_at_date: a.scheduled_at ? new Date(a.scheduled_at) : null }));
        // Sort newest first
        normalized.sort((x, y) => {
          const xd = x._scheduled_at_date ? x._scheduled_at_date.getTime() : 0;
          const yd = y._scheduled_at_date ? y._scheduled_at_date.getTime() : 0;
          return yd - xd;
        });
        setAppointments(normalized);
      }
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch appointments.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load businesses once (used by assign dialog)
  const fetchBusinesses = async () => {
    setLoadingBusinesses(true);
    try {
      const res = await ApiServices.get("external-businesses/");
      const data = res?.data ?? res;
      setBusinesses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: "Failed to load businesses", severity: "error" });
    } finally {
      setLoadingBusinesses(false);
    }
  };

  // Debounced search handler (client-side filtering)
  useEffect(() => {
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      // we do client-side filtering below
    }, 300);
    return () => {
      if (searchRef.current) clearTimeout(searchRef.current);
    };
  }, [search]);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // POST record-payment
  const markAsPaid = async (appointment) => {
    if (!appointment?.id) return;
    try {
      setLoading(true);
      await ApiServices.post(`service-appointments/${appointment.id}/record-payment/`, { payment_intent_id: "pi_simulated" });
      // Update the local state: mark payment_status as PAID
      setAppointments((prev) =>
        prev.map((a) => (a.id === appointment.id ? { ...a, payment_status: "PAID", status: "COMPLETED" } : a))
      );
      setSnack({ open: true, message: "Payment recorded (simulated).", severity: "success" });
    } catch (err) {
      console.error(err);
      let msg = "Failed to record payment.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Helpers
  const formatDateTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      // format: 2025-10-16 14:30
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day} ${hh}:${mm}`;
    } catch {
      return iso;
    }
  };

  const openImageInNewTab = (imgPath) => {
    const url = imgPath.startsWith("http") ? imgPath : `${BASE_URL}${imgPath}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // row color logic priority:
  // 1) paid/completed => green
  // 2) unpaid => red
  // 3) business present but no employee => yellow
  // 4) no business => white
  const rowBgFor = (appt) => {
    const paid = (appt.payment_status ?? "").toUpperCase() === "PAID";
    const completed = (appt.status ?? "").toUpperCase() === "COMPLETED";
    if (paid || completed) return "#e8fff0"; // light green
    const unpaid = !paid;
    if (unpaid) return "#ffe8e8"; // light red
    // If has business but no employee
    if (appt.business && !appt.employee) return "#fff8e1"; // light yellow
    return "#ffffff"; // default white
  };

  // client-side filter + search
  const filteredAppointments = appointments.filter((a) => {
    // payment filter
    if (paymentFilter === "paid" && (a.payment_status ?? "").toUpperCase() !== "PAID") return false;
    if (paymentFilter === "unpaid" && (a.payment_status ?? "").toUpperCase() === "PAID") return false;

    // search across customer name, business name, employee name, status, phone, address
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const custName = `${a.customer?.first_name ?? ""} ${a.customer?.last_name ?? ""}`.toLowerCase();
    const bizName = (a.business?.name ?? "").toLowerCase();
    const empName = `${a.employee?.first_name ?? ""} ${a.employee?.last_name ?? ""}`.toLowerCase();
    const status = (a.status ?? "").toLowerCase();
    const phone = (a.customer?.phone ?? a.employee?.phone ?? a.business?.phone ?? "").toLowerCase();
    const address = (a.address ?? a.customer?.address ?? a.business?.address ?? "").toLowerCase();

    return (
      custName.includes(q) ||
      bizName.includes(q) ||
      empName.includes(q) ||
      status.includes(q) ||
      phone.includes(q) ||
      address.includes(q) ||
      String(a.id).includes(q)
    );
  });

  // Open assign dialog for an appointment
  const openAssignDialog = async (appointment) => {
    setAssignAppointment(appointment);
    setSelectedBusinessId("");
    setCostToBusiness("");
    // fetch businesses if not loaded
    if (!businesses || businesses.length === 0) {
      await fetchBusinesses();
    }
    setAssignOpen(true);
  };

  const closeAssignDialog = () => {
    setAssignOpen(false);
    setAssignAppointment(null);
    setSelectedBusinessId("");
    setCostToBusiness("");
    setAssignLoading(false);
  };

  const submitAssignBusiness = async () => {
    if (!assignAppointment) return;
    if (!selectedBusinessId) {
      setSnack({ open: true, message: "Please select a business", severity: "error" });
      return;
    }
    if (!costToBusiness || isNaN(costToBusiness)) {
      setSnack({ open: true, message: "Please enter a valid cost to business", severity: "error" });
      return;
    }

    setAssignLoading(true);
    try {
      const payload = {
        business_id: Number(selectedBusinessId),
        cost_to_business: String(costToBusiness),
      };
      const res = await ApiServices.post(`service-appointments/${assignAppointment.id}/assign-business/`, payload);
      // assume success: update local appointment: set business to selected business object and admin_cost_to_business
      const assignedBusiness = businesses.find((b) => Number(b.id) === Number(selectedBusinessId)) ?? null;

      setAppointments((prev) =>
        prev.map((a) =>
          a.id === assignAppointment.id
            ? {
                ...a,
                business: assignedBusiness,
                admin_cost_to_business: String(costToBusiness),
              }
            : a
        )
      );

      setSnack({ open: true, message: "Business assigned successfully", severity: "success" });
      closeAssignDialog();
    } catch (err) {
      console.error(err);
      let msg = "Failed to assign business.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box", background: "#fff", minHeight: "70vh" }}>
      <style>{`
        .appointments-header { display:flex; gap:12px; align-items:center; justify-content:space-between; flex-wrap:wrap; margin-bottom:16px; }
        .filter-row { display:flex; gap:12px; align-items:center; flex-wrap:wrap; }
        .thumb-img { width:64px; height:48px; object-fit:cover; border-radius:6px; border:1px solid rgba(0,0,0,0.08); }
        .service-chip { background:#f1f5f9; padding:6px 8px; border-radius:8px; display:inline-block; margin-right:6px; font-size:12px; }
      `}</style>

      <Box className="appointments-header">
        <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700 }}>
          Appointments
        </Typography>

        <Box className="filter-row">
          <TextField
            placeholder="Search customer/business/employee or id..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 320 }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="payment-filter-label">Payment</InputLabel>
            <Select
              labelId="payment-filter-label"
              value={paymentFilter}
              label="Payment"
              onChange={(e) => setPaymentFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" onClick={fetchAppointments} sx={{ backgroundColor: "#094c9f" }}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 900, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>Scheduled</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 220 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 220 }}>Business</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 180 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Services</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 170 }}>Images</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: 140 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredAppointments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No appointments found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAppointments.map((a, idx) => {
                    // compute service summary
                    const services = Array.isArray(a.appointment_services) ? a.appointment_services : [];
                    // collect images from services
                    const images = [];
                    services.forEach((as) => {
                      if (Array.isArray(as.images) && as.images.length > 0) {
                        as.images.forEach((im) => images.push(im));
                      }
                    });

                    const bg = rowBgFor(a);
                    return (
                      <TableRow key={a.id} hover sx={{ backgroundColor: bg }}>
                        <TableCell>{idx + 1}</TableCell>

                        <TableCell>
                          <Typography variant="body2">{formatDateTime(a.scheduled_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.status ?? "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>
                            {a.customer_details ? `${a.customer_details.first_name ?? ""} ${a.customer_details.last_name ?? ""}` : "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.customer_details?.email ?? ""} • {a.customer_details?.phone ?? ""}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {a.business ? (
                            <>
                              <Typography sx={{ fontWeight: 700 }}>{a.business.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.business.email} • {a.business.phone}
                              </Typography>
                            </>
                          ) : (
                            /* If no business: show Assign Business button (white bg by your instruction) */
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddBusinessIcon />}
                              onClick={() => openAssignDialog(a)}
                              sx={{ textTransform: "none" }}
                            >
                              Assign business
                            </Button>
                          )}
                        </TableCell>

                        <TableCell>
                          {a.employee ? (
                            <>
                              <Typography sx={{ fontWeight: 700 }}>{`${a.employee.first_name} ${a.employee.last_name}`}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.employee.email} • {a.employee.phone}
                              </Typography>
                            </>
                          ) : (
                            <Typography color="text.secondary">No employee assigned</Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {services.length}
                          </Typography>
                          {services.map((s) => (
                            <Box key={s.id} sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Service #{s.service} — {parseFloat(s.price_snapshot ?? 0).toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{parseFloat(a.total_price ?? 0).toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Admin cost: {parseFloat(a.admin_cost_to_business ?? 0).toFixed(2)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{a.payment_status ?? "-"}</Typography>
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            {images.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No images
                              </Typography>
                            ) : (
                              images.map((im) => {
                                const url = im.image?.startsWith("http") ? im.image : `${BASE_URL}${im.image}`;
                                const label = im.is_before ? "Before" : "After";
                                return (
                                  <Tooltip key={im.id} title={`${label} • ${im.uploaded_by}`}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <img
                                        src={url}
                                        alt={label}
                                        className="thumb-img"
                                        onClick={() => openImageInNewTab(im.image)}
                                        style={{ cursor: "pointer" }}
                                      />
                                      <IconButton size="small" onClick={() => openImageInNewTab(im.image)} aria-label="open">
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Tooltip>
                                );
                              })
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          {(a.payment_status ?? "").toUpperCase() !== "PAID" ? (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PaidIcon />}
                              onClick={() => markAsPaid(a)}
                              disabled={loading}
                              sx={{ backgroundColor: "#094c9f", textTransform: "none" }}
                            >
                              Mark as Paid
                            </Button>
                          ) : (
                            <Typography variant="body2" sx={{ color: "green", fontWeight: 700 }}>
                              Paid
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Assign Business Dialog */}
      <Dialog open={assignOpen} onClose={closeAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Business</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="assign-business-label">{loadingBusinesses ? "Loading..." : "Business"}</InputLabel>
              <Select
                labelId="assign-business-label"
                value={selectedBusinessId}
                label="Business"
                onChange={(e) => setSelectedBusinessId(e.target.value)}
              >
                {businesses.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name} — {b.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Cost to business"
              value={costToBusiness}
              onChange={(e) => setCostToBusiness(e.target.value)}
              type="number"
              inputProps={{ step: "0.01", min: 0 }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeAssignDialog} disabled={assignLoading}>
            Cancel
          </Button>
          <Button onClick={submitAssignBusiness} variant="contained" disabled={assignLoading} sx={{ backgroundColor: "#094c9f" }}>
            {assignLoading ? <CircularProgress size={18} /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppointmentsList;
